import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useShamanContext } from './useShamanContext';
import { formatDateTimeKo, formatKRW, formatPhone } from '@/lib/format';

const STATUS_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'pending_payment', label: '대기' },
  { value: 'confirmed', label: '확정' },
  { value: 'completed', label: '완료' },
  { value: 'cancelled', label: '취소' },
  { value: 'no_show', label: '노쇼' },
];

interface BookingRow {
  id: string;
  starts_at: string;
  status: string;
  duration_minutes: number;
  price_krw: number;
  payment_method: string;
  customer_name: string;
  customer_phone: string;
  customer_birth_date: string;
  customer_gender: string;
}

export function ShamanBookingsPage() {
  const { data: ctx } = useShamanContext();
  const shamanId = ctx?.shaman.id;
  const [status, setStatus] = useState<string>('all');

  const q = useQuery({
    queryKey: ['shaman-bookings', shamanId, status],
    enabled: !!shamanId,
    queryFn: async (): Promise<BookingRow[]> => {
      let query = supabase
        .from('bookings')
        .select('id, starts_at, status, duration_minutes, price_krw, payment_method, customer_name, customer_phone, customer_birth_date, customer_gender')
        .eq('shaman_id', shamanId!)
        .order('starts_at', { ascending: false })
        .limit(100);
      if (status === 'cancelled') {
        query = query.in('status', ['cancelled_by_customer', 'cancelled_by_shaman']);
      } else if (status !== 'all') {
        query = query.eq('status', status);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <div className="space-y-3">
      <div className="card">
        <label className="label">필터</label>
        <select className="field" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {q.isLoading && <div className="text-ink-muted">불러오는 중…</div>}
      {!q.isLoading && !q.data?.length && (
        <div className="card text-ink-muted text-center">예약이 없습니다.</div>
      )}

      <ul className="space-y-3">
        {q.data?.map((b) => (
          <li key={b.id} className="card space-y-1">
            <div className="flex justify-between items-baseline">
              <div className="font-bold">{formatDateTimeKo(b.starts_at)}</div>
              <div className="text-base text-ink-muted">{formatKRW(b.price_krw)}</div>
            </div>
            <div className="text-base">
              {b.customer_name} ({b.customer_gender === 'male' ? '남' : '여'} · {b.customer_birth_date}) · {formatPhone(b.customer_phone)}
            </div>
            <div className="text-base text-ink-muted">
              {b.duration_minutes}분 · {b.payment_method === 'toss_pay' ? '토스/카드' : '가상계좌'} · {b.status}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
